import React, { Component } from "react";
import { Col, Row, FormGroup, Label, Modal } from "reactstrap";
import { AvForm, AvField } from "availity-reactstrap-validation";
import {
  createFoodAddOn,
  getFoodAddOnList,
  getFoodAddOnById,
  updateFoodAddOn,
  removeFoodAddOn,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, Table, Btn, IconBtn, SearchPill, StatRow, StatCard, MeterList, PanelLink, ConfirmDialog,
} from "../../components/Console/ConsoleUI";
import { canWrite, hasModuleAccess } from "../../services/PermissionService";

/**
 * Food add-ons — the extras a guest can put on a dish, restyled onto the
 * console shell.
 *
 * The record is a name and a price and nothing more (the list endpoint strips
 * isActive and both timestamps), so the summaries here are arithmetic over
 * that one payload: how many extras, what they average, how many are free.
 * Nothing about how often an add-on is actually ordered is available, and none
 * is implied.
 *
 * Re-skin only: createFoodAddOn / updateFoodAddOn are called with exactly the
 * object the previous screen sent.
 */

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

class FoodAddOn extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      isLoaded: false,
      modal: false,
      foodAddOnName: "",
      amount: "",
      foodAddOnId: "",
      handleResponse: null,
      // View-only state — never changes what is fetched.
      search: "",
      showDeleteModal: false,
      pendingDelete: null,
      actionLoading: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.toggle = this.toggle.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.getALlluser = this.getALlluser.bind(this);
  }

  toggle() {
    this.setState({
      modal: !this.state.modal,
      foodAddOnName: "",
      amount: "",
      foodAddOnId: "",
    });
  }

  handleCloseModal() {
    this.setState({
      modal: false,
    });
  }

  handleChange = (e) => {
    this.setState({
      [e.target.id]: e.target.value,
    });
  };

  handleSubmit() {
    // AvForm has already blocked the browser's own submit before calling this.
    const userObject = {
      foodAddOnName: this.state.foodAddOnName,
      amount: this.state.amount,
      foodAddOnId: this.state.foodAddOnId,
    };

    if (this.state.foodAddOnId == "" || this.state.foodAddOnId == null) {
      createFoodAddOn(userObject)
        .then((response) => {
          if (response.status !== 200) {
            toast.error(response.message, {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          } else {
            this.setState({
              handleResponse: {
                isSuccess: response.status === 200,
                message: "success",
                foodAddOnName: "",
                amount: "",
                foodAddOnId: "",
              },
            });
            this.getALlluser();
            this.handleCloseModal();

            toast.success("FoodAddOn added successfully");
          }
        })
        .catch((error) => {
          toast.error("error");
        });
    } else {
      updateFoodAddOn(userObject)
        .then((updateResults) => {
          if (updateResults.status !== 200) {
            toast.error(updateResults.message, {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          } else {
            this.setState({
              handleResponse: {
                isSuccess: updateResults.status === 200,
                message: "success",
                foodAddOnId: "",
              },
            });
            this.getALlluser();
            this.handleCloseModal();

            toast.success("FoodAddOn  updated successfully");
          }
        })
        .catch((error) => {
          toast.error("error");
        });
    }
  }

  getALlluser() {
    getFoodAddOnList().then((result) => {
      this.setState({
        isLoaded: true,
        items: (result && result.data) || [],
      });
    });
  }

  removeFoodAdd(id) {
    this.setState({ actionLoading: true });
    removeFoodAddOn(id)
      .then((results) => {
        this.getALlluser();
      })
      .catch((error) => {
        toast.error("error");
      })
      .finally(() => {
        this.setState({
          actionLoading: false,
          showDeleteModal: false,
          pendingDelete: null,
        });
      });
  }

  componentDidMount() {
    this.getALlluser();
  }

  editFoodAddOn(id) {
    getFoodAddOnById(id)
      .then((results) => {
        this.setState({
          foodAddOnName: results.data.foodAddOnName,
          amount: results.data.amount,
          foodAddOnId: results.data._id,
        });
      })
      .catch((error) => {
        toast.error("error");
      });
    this.setState({ modal: true });
  }

  // --- presentation ---------------------------------------------------------

  askDelete = (a) => this.setState({ showDeleteModal: true, pendingDelete: a });

  toggleDeleteModal = () =>
    this.setState({
      showDeleteModal: !this.state.showDeleteModal,
      pendingDelete: null,
    });

  /** Client-side filter over the loaded list. No request is made. */
  visibleAddOns() {
    const q = this.state.search.trim().toLowerCase();
    if (!q) return this.state.items || [];
    return (this.state.items || []).filter((a) =>
      String(a.foodAddOnName || "").toLowerCase().includes(q)
    );
  }

  /** The dearest extras, biggest first — bars are share of the dearest one. */
  priciest() {
    const items = (this.state.items || []).filter((a) => Number(a.amount) > 0);
    if (items.length === 0) return [];
    const sorted = items
      .slice()
      .sort((a, b) => Number(b.amount) - Number(a.amount))
      .slice(0, 5);
    const top = Number(sorted[0].amount) || 1;
    return sorted.map((a) => ({
      label: a.foodAddOnName || "Untitled",
      value: money(a.amount),
      pct: (Number(a.amount) / top) * 100,
    }));
  }

  columns() {
    const write = canWrite();
    return [
      {
        key: "foodAddOnName",
        label: "Add-on",
        render: (a) => (
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <span
              className="ms"
              style={{
                fontSize: 19,
                color: C.brown,
                background: C.iconTint,
                padding: 7,
                borderRadius: 10,
              }}
            >
              add_circle
            </span>
            <span style={{ fontSize: 15, color: C.ink }}>
              {a.foodAddOnName || "Untitled add-on"}
            </span>
          </div>
        ),
      },
      {
        key: "amount",
        label: "Price",
        width: 160,
        align: "right",
        render: (a) =>
          Number(a.amount) > 0 ? (
            <span style={{ fontSize: 15, color: C.ink }}>{money(a.amount)}</span>
          ) : (
            <span style={{ color: C.taupeLight }}>Free</span>
          ),
      },
      // Write controls stay behind canWrite(): a view-only account still sees
      // every add-on and its price, just no way to change one.
      ...(write
        ? [
            {
              key: "actions",
              label: "",
              width: 96,
              align: "right",
              render: (a) => (
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <IconBtn
                    icon="edit"
                    title={`Edit ${a.foodAddOnName || "add-on"}`}
                    onClick={() => this.editFoodAddOn(a._id.toString())}
                  />
                  <IconBtn
                    icon="delete"
                    tone="bad"
                    title={`Remove ${a.foodAddOnName || "add-on"}`}
                    onClick={() => this.askDelete(a)}
                  />
                </div>
              ),
            },
          ]
        : []),
    ];
  }

  renderModal() {
    const { foodAddOnId } = this.state;
    const editing = !!foodAddOnId;

    return (
      <Modal isOpen={this.state.modal} toggle={this.toggle} className="con-modal" centered>
        <div style={{ padding: 30 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span
              className="ms"
              style={{
                fontSize: 26,
                color: C.brown,
                background: C.iconTint,
                padding: 13,
                borderRadius: 16,
              }}
            >
              add_circle
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 500, color: C.ink }}>
                {editing ? "Edit add-on" : "Add an add-on"}
              </div>
              <div style={{ marginTop: 4, fontSize: 14, color: C.taupe }}>
                {editing
                  ? "The new price applies to orders placed from now on"
                  : "An extra a guest can add to a dish at checkout"}
              </div>
            </div>
            <button
              type="button"
              onClick={this.toggle}
              aria-label="Close"
              style={{
                border: 0,
                background: "transparent",
                color: C.taupeLight,
                cursor: "pointer",
                padding: 4,
                lineHeight: 1,
              }}
            >
              <span className="ms" style={{ fontSize: 22 }}>close</span>
            </button>
          </div>

          <AvForm onValidSubmit={this.handleSubmit}>
            <div style={{ marginTop: 24 }}>
              <Row form>
                <Col md={7}>
                  <FormGroup>
                    <Label for="foodAddOnName">Add-on name</Label>
                    <AvField
                      type="text"
                      value={this.state.foodAddOnName}
                      onChange={this.handleChange}
                      id="foodAddOnName"
                      name="foodAddOnName"
                      placeholder="e.g. Extra cheese"
                      required
                                  errorMessage="Add-on name is required"
                                />
                  </FormGroup>
                </Col>
                <Col md={5}>
                  <FormGroup>
                    <Label for="amount">Price</Label>
                    <AvField
                      type="number"
                      min={1}
                      value={this.state.amount}
                      onChange={this.handleChange}
                      id="amount"
                      name="amount"
                      placeholder="0"
                    />
                  </FormGroup>
                </Col>
              </Row>
              <div style={{ fontSize: 12, color: C.taupeLight }}>
                Leave the price empty for an extra that costs nothing.
              </div>
            </div>

            <div style={{ marginTop: 22, display: "flex", gap: 12 }}>
              <button
                type="button"
                onClick={this.toggle}
                style={{
                  flex: 1,
                  height: 52,
                  border: "1px solid #e6d8c4",
                  borderRadius: 26,
                  background: "#fff",
                  color: C.slate,
                  fontFamily: "inherit",
                  fontSize: 15,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              {/* Stays a real submit button — AvForm validates on submit. */}
              <button
                type="submit"
                style={{
                  flex: 1,
                  height: 52,
                  border: 0,
                  borderRadius: 26,
                  background: C.brown,
                  color: "#fff",
                  fontFamily: "inherit",
                  fontSize: 15,
                  cursor: "pointer",
                }}
              >
                {editing ? "Save changes" : "Add add-on"}
              </button>
            </div>
          </AvForm>
        </div>
      </Modal>
    );
  }

  render() {
    const { items, isLoaded, search, pendingDelete } = this.state;
    const write = canWrite();
    const visible = this.visibleAddOns();
    const paid = (items || []).filter((a) => Number(a.amount) > 0);
    const avg =
      paid.length > 0
        ? paid.reduce((s, a) => s + Number(a.amount), 0) / paid.length
        : 0;

    return (
      <ConsolePage
        title="Food add-ons"
        subtitle={
          isLoaded
            ? `${(items || []).length} add-on${(items || []).length === 1 ? "" : "s"}${
                search.trim() ? ` · ${visible.length} matching` : ""
              }`
            : "Loading add-ons…"
        }
        actions={
          <React.Fragment>
            <SearchPill
              value={search}
              onChange={(v) => this.setState({ search: v })}
              placeholder="Search add-ons"
              width={220}
            />
            {write && (
              <Btn icon="add" onClick={this.toggle}>
                Add add-on
              </Btn>
            )}
          </React.Fragment>
        }
      >

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <StatRow columns={3}>
            <StatCard icon="add_circle" value={(items || []).length} label="Add-ons" />
            <StatCard icon="payments" value={money(avg)} label="Average price" />
            <StatCard
              icon="money_off"
              value={(items || []).length - paid.length}
              label="Free of charge"
            />
          </StatRow>

          <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 20, alignItems: "start" }}>
            <Panel style={{ padding: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "16px 22px",
                  borderBottom: `1px solid ${C.panel}`,
                }}
              >
                <div>
                  <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>All add-ons</div>
                  <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight }}>
                    Offered against a dish at checkout in the guest app
                  </div>
                </div>
                <div style={{ fontSize: 14, color: C.taupeLight }}>
                  {visible.length} of {(items || []).length} shown
                </div>
              </div>

              <div style={{ padding: "6px 8px" }}>
                {!isLoaded ? (
                  <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                    Loading add-ons…
                  </div>
                ) : (
                  <Table
                    columns={this.columns()}
                    rows={visible}
                    empty={
                      (items || []).length === 0
                        ? "No add-ons yet — add the first extra guests can order"
                        : "No add-on matches that search."
                    }
                  />
                )}
              </div>
            </Panel>

            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <Panel
                title="Dearest extras"
                subtitle="Priced against the dearest one on the list"
                style={{ display: "flex", flexDirection: "column", minHeight: 240 }}
              >
                <MeterList items={this.priciest()} empty="No priced add-ons yet" />
                <div style={{ marginTop: 16, fontSize: 12, color: C.taupeLight, lineHeight: 1.6 }}>
                  The add-on record holds a name and a price only, so how often
                  each is ordered is not shown here.
                </div>
                {hasModuleAccess("FOOD") && (
                  <PanelLink onClick={() => this.props.history.push("/Food")}>
                    Food menu
                  </PanelLink>
                )}
              </Panel>
            </div>
          </div>
        </div>

        {this.renderModal()}

        <ConfirmDialog
          isOpen={this.state.showDeleteModal}
          toggle={this.toggleDeleteModal}
          onConfirm={() =>
            pendingDelete && this.removeFoodAdd(pendingDelete._id.toString())
          }
          icon="delete"
          title={`Remove ${pendingDelete ? pendingDelete.foodAddOnName : "this add-on"}?`}
          body="Guests stop being offered it at checkout straight away."
          tip="Deleting cannot be undone — orders already placed keep the add-on they were charged for."
          confirmText="Remove"
          loading={this.state.actionLoading}
        />
      </ConsolePage>
    );
  }
}

export default FoodAddOn;
